import React, { useMemo, useRef, useState } from 'react';
import { Check, Copy as CopyIcon } from 'lucide-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { GeneratedFile } from '@Models';
import InlineMarkdownImage, { InlineImageContext } from './InlineMarkdownImage';

interface MarkdownMessageProps {
  content: string;
  generatedFiles?: GeneratedFile[];
  conversationId?: string;
  interactionTimestamp?: string;
  onExpand?: (file: GeneratedFile, url: string) => void;
}

type CodeProps = {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLElement>;

const nodeToText = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map((n: React.ReactNode) => nodeToText(n)).join('');
  if (typeof node === 'object' && 'props' in (node as Record<string, unknown>)) {
    const element = node as { props?: { children?: React.ReactNode } };
    return nodeToText(element.props?.children);
  }
  return '';
};

const CodeBlock: React.FC<CodeProps> = ({ inline, className, children, ...rest }) => {
  const isInline = Boolean(inline);
  if (isInline) {
    return (
      <code className="rounded bg-gray-50 px-1 py-0.5 font-mono text-[0.85em] text-gray-800" {...rest}>
        {children}
      </code>
    );
  }

  const [copied, setCopied] = useState(false);
  const codeRef = useRef<HTMLElement | null>(null);
  const languageMatch = /language-(\w+)/.exec(className || '');
  const languageLabel = languageMatch ? languageMatch[1] : undefined;

  const getText = (): string => {
    const fromRef = codeRef.current?.innerText;
    if (fromRef && fromRef.trim().length > 0) return fromRef;
    return nodeToText(children);
  };

  const handleCopy = () => {
    const text = getText();
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {
        setCopied(false);
      });
  };

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-gray-200 bg-[#fbfaf9]">
      <div className="flex items-center justify-between border-b border-gray-200 bg-[#fbfaf9] px-3 py-1.5">
        <span className="text-xs uppercase tracking-wide text-gray-400">{languageLabel || 'code'}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-800 cursor-pointer hover:bg-gray-50 transition-colors focus:outline-none"
          aria-label={copied ? 'Copied' : 'Copy code'}
          title={copied ? 'Copied' : 'Copy'}>
          {copied ? <Check className="h-3.5 w-3.5 text-success-500" /> : <CopyIcon className="h-3.5 w-3.5" />}
        </button>
      </div>
      <pre className="overflow-x-auto bg-transparent p-3" onDoubleClick={handleCopy}>
        <code
          ref={codeRef as React.MutableRefObject<HTMLElement>}
          className={`${className || ''} font-mono text-gray-800`}>
          {children}
        </code>
      </pre>
    </div>
  );
};

// Module-scoped so identities stay stable: inline components remount on every render, resetting table scroll.
const remarkPlugins = [remarkGfm, remarkBreaks];

const markdownComponents: Components = {
  a: ({ children, ...props }) => (
    <a
      {...props}
      className="underline decoration-[#7c6cf6]/30 hover:decoration-[#7c6cf6]"
      style={{ color: '#7c6cf6' }}
      target="_blank"
      rel="noreferrer noopener">
      {children}
    </a>
  ),
  strong: ({ children, ...props }) => (
    <strong {...props} className="font-semibold text-gray-900">
      {children}
    </strong>
  ),
  em: ({ children, ...props }) => (
    <em {...props} className="italic text-gray-700">
      {children}
    </em>
  ),
  h1: ({ children, ...props }) => (
    <h1 {...props} className="mt-5 mb-3 text-2xl font-semibold text-gray-900">
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 {...props} className="mt-5 mb-2 text-xl font-semibold text-gray-900">
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 {...props} className="mt-4 mb-2 text-lg font-semibold text-gray-900">
      {children}
    </h3>
  ),
  h4: ({ children, ...props }) => (
    <h4 {...props} className="mt-3 mb-1.5 text-base font-semibold text-gray-900">
      {children}
    </h4>
  ),
  p: ({ node, children, ...props }) => {
    // The inline card is block-level, so a paragraph holding an image renders as a div.
    const hasImage = node.children.some((child) => child.type === 'element' && child.tagName === 'img');
    const Tag = hasImage ? 'div' : 'p';
    return (
      <Tag {...props} className="mb-3 leading-relaxed text-gray-900">
        {children}
      </Tag>
    );
  },
  // Survives only because react-markdown 8 leaves `img.src` untransformed; v9+ would blank `file:`.
  img: (props) => <InlineMarkdownImage {...props} />,
  code: (props: CodeProps) => <CodeBlock {...props} />,
  table: ({ children, ...props }) => (
    <div className="-mx-1 my-3 overflow-x-auto rounded-lg border border-gray-200">
      <table {...props} className="min-w-full bg-[#fbfaf9] text-left text-sm [&_tr:last-child_td]:border-b-0">
        {children}
      </table>
    </div>
  ),
  thead: (props) => <thead {...props} className="bg-[#ffffff]" />,
  tr: ({ isHeader: _, ...props }) => <tr {...props} className="even:bg-[#ffffff]" />,
  th: ({ isHeader: _, ...props }) => (
    <th {...props} className="whitespace-nowrap border-b border-gray-200 px-3 py-2 font-semibold text-gray-900" />
  ),
  td: ({ isHeader: _, ...props }) => (
    <td {...props} className="border-b border-gray-200 px-3 py-2 align-top text-gray-700" />
  ),
  ul: ({ ordered: _, ...props }) => <ul {...props} className="my-2 list-disc space-y-1 pl-5" />,
  ol: ({ ordered: _, ...props }) => <ol {...props} className="my-2 list-decimal space-y-1 pl-5" />,
  li: ({ ordered: _, index: _i, ...props }) => <li {...props} className="leading-relaxed text-gray-900" />,
  blockquote: ({ children, ...props }) => (
    <blockquote
      {...props}
      className="my-3 border-l-2 pl-3 text-gray-700"
      style={{ borderColor: 'rgba(124, 108, 246, 0.45)' }}>
      {children}
    </blockquote>
  ),
};

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({
  content,
  generatedFiles,
  conversationId,
  interactionTimestamp,
  onExpand,
}) => {
  const inlineImages = useMemo(
    () => ({ generatedFiles, conversationId, timestamp: interactionTimestamp, onExpand }),
    [generatedFiles, conversationId, interactionTimestamp, onExpand],
  );

  return (
    <InlineImageContext.Provider value={inlineImages}>
      <div className="max-w-none text-[15px] leading-relaxed text-gray-900">
        <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
          {content}
        </ReactMarkdown>
      </div>
    </InlineImageContext.Provider>
  );
};

export default React.memo(MarkdownMessage);
